import React, { useEffect, useMemo, useState } from 'react';
import { LeftOutlined, RightOutlined, CloseOutlined } from '@ant-design/icons';
import type { MeasureMode } from '../../../../hooks/useMeasureTool';
import './measurePanel.scss';

interface UnitOption {
  label: string;
  /** Multiplier applied to the base value (metres / square metres). */
  factor: number;
}

// US-102820 Sc7 — distance units. Base measurement is kept in metres; only the
// display factor changes when cycling, so the geometry is never re-measured.
const DISTANCE_UNITS: UnitOption[] = [
  { label: 'METERS', factor: 1 },
  { label: 'KILOMETERS', factor: 1 / 1000 },
  { label: 'MILES', factor: 1 / 1609.344 },
  { label: 'FEET', factor: 1 / 0.3048 },
];

// US-102821 Sc9 — area units. Base measurement is kept in square metres.
const AREA_UNITS: UnitOption[] = [
  { label: 'SQ. M', factor: 1 },
  { label: 'SQ. KM', factor: 1 / 1_000_000 },
  { label: 'SQ. MI', factor: 1 / 2_589_988.110336 },
  { label: 'SQ. FT', factor: 1 / 0.09290304 },
];

interface MeasurePanelProps {
  show: boolean;
  mode: MeasureMode;
  setMode: (mode: MeasureMode) => void;
  /** Cumulative geodesic distance in metres. */
  distanceMeters: number;
  /** Geodesic area in square metres. */
  areaSqMeters: number;
  onReset: () => void;
  onClose: () => void;
  error: string | null;
}

const formatValue = (value: number): string => {
  if (!Number.isFinite(value) || value === 0) return '0';
  const abs = Math.abs(value);
  if (abs >= 100) return Math.round(value).toLocaleString();
  if (abs >= 1) return parseFloat(value.toFixed(2)).toString();
  if (abs >= 0.01) return parseFloat(value.toFixed(3)).toString();
  return value.toExponential(2);
};

export const DistanceIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    {/* Clean diagonal slash */}
    <path d="M19 5L5 19" stroke="currentColor" strokeWidth="1" strokeLinecap="square" />
  </svg>
);

export const AreaIcon = () => (
  <svg className="area-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    {/* Outline triangle pointing top-left */}
    <path d="M4 20V4H20L4 20Z" stroke="currentColor" strokeWidth="1" strokeLinejoin="miter" fill="none" />
  </svg>
);

export const PointIcon = () => (
  <svg className="point-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    {/* Outlined circle centered in the frame */}
    <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1" fill="currentColor" />
  </svg>
);

/**
 * US-102820 / US-102821 — the Measure Tool panel, docked bottom-left over the
 * map. Distance/Area tabs, the live value, a unit cycler (◀ / ▶), Reset and a
 * Close (X). Unit selection is local display state; the measured geometry is
 * owned by `useMeasureTool` and is unaffected by unit changes.
 */
const MeasurePanel: React.FC<MeasurePanelProps> = ({ show, mode, setMode, distanceMeters, areaSqMeters, onReset, onClose, error }) => {
  const [distanceUnit, setDistanceUnit] = useState(0);
  const [areaUnit, setAreaUnit] = useState(0);

  const units = mode === 'area' ? AREA_UNITS : DISTANCE_UNITS;
  const unitIndex = mode === 'area' ? areaUnit : distanceUnit;
  const setUnitIndex = mode === 'area' ? setAreaUnit : setDistanceUnit;
  const base = mode === 'area' ? areaSqMeters : distanceMeters;

  // Reset the unit back to the default whenever the panel is dismissed.
  useEffect(() => {
    if (!show) {
      setDistanceUnit(0);
      setAreaUnit(0);
    }
  }, [show]);

  const displayValue = useMemo(() => formatValue(base * units[unitIndex].factor), [base, units, unitIndex]);

  useEffect(onReset, [mode]);

  if (!show) return null;

  const cycleUnit = (step: number) => setUnitIndex((prev) => (prev + step + units.length) % units.length);

  return (
    <div className={`measure-panel ${error ? 'measure-panel--error' : ''}`}>
      <button type="button" className="measure-panel__close" onClick={onClose} aria-label="Close">
        <CloseOutlined />
      </button>

      <div className="measure-panel__tabs">
        <button type="button" className={`measure-panel__tab ${mode === 'distance' ? 'is-active' : ''}`} onClick={() => setMode('distance')}>
          <span className="measure-panel__tab-icon">
            <DistanceIcon />
          </span>
          DISTANCE
        </button>
        <button type="button" className={`measure-panel__tab ${mode === 'area' ? 'is-active' : ''}`} onClick={() => setMode('area')}>
          <span className="measure-panel__tab-icon">
            <AreaIcon />
          </span>
          AREA
        </button>
      </div>

      {/* Scenario 1 & 2: Show error message or calculated value */}
      <div className="measure-panel__content">
        {error ? (
          <div className="measure-panel__error-msg">{error}</div>
        ) : (
          <div className="measure-panel__value" title={displayValue}>
            {displayValue}
          </div>
        )}
      </div>

      {!error && (
        <div className="measure-panel__unit">
          <button type="button" className="measure-panel__unit-arrow" onClick={() => cycleUnit(-1)} aria-label="Previous unit">
            <LeftOutlined />
          </button>
          <span className="measure-panel__unit-label">{units[unitIndex].label}</span>
          <button type="button" className="measure-panel__unit-arrow" onClick={() => cycleUnit(1)} aria-label="Next unit">
            <RightOutlined />
          </button>
        </div>
      )}

      <button type="button" className="measure-panel__reset" onClick={onReset}>
        RESET
      </button>
    </div>
  );
};

export default MeasurePanel;
